import { addMinutes, format, parseISO } from 'date-fns';
import { SelectedDates } from '@rossum/components/date-time-range-picker/types';
import { DateOperators, dateOperators, FilterOperator } from '../mql/operators';
import {
  FilterColDef,
  FilterContext,
  FilterItem,
  FilterValues,
  isFilterContextSimple,
} from './types';

type DateOperator = DateOperators[number];
type DateOperatorKeys = DateOperator['value'];

export const checkIfOperatorIsDate = (filterContext: FilterContext) =>
  isFilterContextSimple(filterContext)
    ? filterContext.operator.type === 'date'
    : filterContext.every(({ operator }) => operator.type === 'date');

export const isDateOperator = (
  operator: string
): operator is DateOperator['value'] => {
  return dateOperators.some(({ value }) => value === operator);
};

export const beforeOperator: DateOperator = {
  type: 'date',
  value: 'before',
  component: 'dateTimePicker',
};
export const afterOperator: DateOperator = {
  type: 'date',
  value: 'onOrAfter',
  component: 'dateTimePicker',
};

export const createDateRangeFilterQuery = ({
  from,
  to,
}: {
  from: Date;
  to: Date;
}): FilterContext => {
  return [
    {
      operator: afterOperator,
      value: dateToQuery(from),
    },
    {
      operator: beforeOperator,
      value: dateToQuery(to),
    },
  ];
};

const createEndingDate = (date: Date) => ({ endingDate: date });
const createStartingDate = (date: Date) => ({ startingDate: date });

const operatorNormalizers: Partial<
  Record<
    FilterOperator['value'],
    typeof createEndingDate | typeof createStartingDate
  >
> = {
  after: createStartingDate,
  onOrAfter: createStartingDate,
  before: createEndingDate,
  onOrBefore: createEndingDate,
};

// isNaN(Invalid Date) == true
export const getValidDate = (value: unknown) =>
  typeof value === 'string'
    ? parseISO(value)
    : value instanceof Date && !Number.isNaN(value)
      ? value
      : null;

export const processAdvancedOperatorValues = ({
  filterContext,
  column,
}: {
  filterContext: FilterValues[];
  column: FilterColDef;
}) =>
  filterContext.reduce<{
    endingDate: SelectedDates[number];
    startingDate: SelectedDates[number];
  }>(
    (acc, { operator, value }) => {
      const validValue = getValidDate(value);
      const normalizedValue = validValue
        ? processDateColumnValue({
            date: validValue,
            column,
            direction: 'localizer',
          })
        : null;

      if (!normalizedValue) return acc;

      const normalizer = operatorNormalizers[operator.value];

      return normalizer ? { ...acc, ...normalizer(normalizedValue) } : acc;
    },
    { endingDate: null, startingDate: null }
  );

export const resolveDateOperatorValue = ({
  endingDate,
  startingDate,
}: ReturnType<typeof processAdvancedOperatorValues>) =>
  !endingDate || !startingDate ? null : 'is';

// Considering bookmarked urls;
// A filter is considered advanced only when the same column has multiple operators.
// In Date's case an advanced filter is valid only when we receive a combination of 'before' and 'after' operators.
// We disregard other combinations because they don't make sense and they were there for historical reasons.
// This function will return either a refined operatorValue in case of valid date filters, or null in case of invalid.
export const getValidatedDateContext = (
  filterItem: FilterItem
): {
  operatorValue: DateOperatorKeys | null;
  existingDates: SelectedDates;
} => {
  const { filterContext, column } = filterItem;

  if (isFilterContextSimple(filterContext)) {
    const operatorValue =
      filterContext.operator.type === 'date'
        ? filterContext.operator.value
        : null;

    const validValue = getValidDate(filterContext.value);

    const normalizedValue = validValue
      ? processDateColumnValue({
          date: validValue,
          column,
          direction: 'localizer',
        })
      : null;

    return {
      operatorValue,
      existingDates: [normalizedValue, normalizedValue],
    };
  }

  const { endingDate, startingDate } = processAdvancedOperatorValues({
    filterContext,
    column,
  });

  const operatorValue = resolveDateOperatorValue({ endingDate, startingDate });

  return {
    operatorValue,
    existingDates:
      operatorValue === null
        ? [null, null]
        : operatorValue === 'is'
          ? [startingDate, endingDate]
          : [endingDate, startingDate],
  };
};

export const convertToUTC = (date: Date) =>
  addMinutes(date, date.getTimezoneOffset());

export const convertFromUTC = (date: Date) =>
  addMinutes(date, -date.getTimezoneOffset());

export const DATE_FORMAT = "yyyy-MM-dd'T'HH:mm";
export const dateToQuery = (date: Date) => format(date, DATE_FORMAT);

export const processDateColumnValue = ({
  date,
  column,
  direction,
}: {
  date: Date;
  column: FilterColDef;
  direction: 'localizer' | 'normalizer';
}) =>
  // Having dataType means it's a schema column
  // Schema columns are not localized to the user's timezone
  column.dataType
    ? date
    : direction === 'normalizer'
      ? convertToUTC(date)
      : convertFromUTC(date);

export const processDatesBeforeSubmit = ({
  selectedDates,
  operator,
  column,
}: {
  selectedDates: SelectedDates;
  operator: FilterOperator;
  column: FilterColDef;
}): FilterContext | null => {
  const [from, to] = selectedDates.map(date =>
    date
      ? processDateColumnValue({
          date,
          column,
          direction: 'normalizer',
        })
      : null
  );

  if (operator.value === 'is' && from && to) {
    return createDateRangeFilterQuery({
      from,
      to,
    });
  }

  if (from) {
    return { operator, value: dateToQuery(from) };
  }

  return null;
};
